export type ICountry = 'SE' | 'NO' | 'GB' | 'FR';
export type IDomain =
  | 'kry.se'
  | 'kry.no'
  | 'livi.co.uk'
  | 'kry.co.uk'
  | 'kry.fr'
  | 'livi.fr'
  | 'embeddable.livi.it'
  | LimeDomain

  // Universal docker-compose domains.
  | 'localhost.se'
  | 'localhost.no'
  | 'localhost.co.uk'
  | 'localhost.fr';

const limeEnvs = {
  test: 'livi.member.test.lime.global',
  staging: 'livi.member.stage.lime.global',
  production: 'livi.member.limeoncall.co.uk',
} as const;
const limeDomains = Object.values(limeEnvs);
type LimeDomain = typeof limeDomains[number];

const getProtocol = () => {
  return `${window.location.protocol}//`;
};

const getDomain = (): IDomain => {
  const host = window.location.host.toLowerCase();
  const envCountry = process.env.REACT_APP_FORCE_COUNTRY;
  const rawBrand = process.env.REACT_APP_FORCE_BRAND?.trim().toLowerCase();
  const envBrand =
    rawBrand === 'kry' || rawBrand === 'livi' ? rawBrand : undefined;

  // Getting the domain from a url is surprisingly difficult, given multiple (or no) subdomains,
  // second-level domains etc, it's just not worth the hassle.
  switch (true) {
    case host.includes('kry.se') || envCountry === 'SE':
      return 'kry.se';
    case host.includes('kry.no') || envCountry === 'NO':
      return 'kry.no';
    case host.includes('kry.co.uk') ||
      (envCountry === 'GB' && envBrand === 'kry'):
      return 'kry.co.uk';
    case host.includes('livi.co.uk') || envCountry === 'GB':
      return 'livi.co.uk';
    case host.includes('embeddable.livi.it') || envCountry === 'GB':
      return 'embeddable.livi.it';
    case limeDomains.some((d) => host === d) || envCountry === 'GB':
      return host as LimeDomain;
    case host.includes('kry.fr') || (envCountry === 'FR' && envBrand === 'kry'):
      return 'kry.fr';
    case host.includes('livi.fr') || envCountry === 'FR':
      return 'livi.fr';

    // Universal docker-compose domains.
    case host.includes('localhost.se'):
      return 'localhost.se';
    case host.includes('localhost.no'):
      return 'localhost.no';
    case host.includes('localhost.co.uk'):
      return 'localhost.co.uk';
    case host.includes('localhost.fr'):
      return 'localhost.fr';

    default:
      return 'kry.se';
  }
};

const getCountry = (): ICountry => {
  const domain = getDomain();

  switch (domain) {
    case 'kry.se':
    case 'localhost.se':
      return 'SE';
    case 'kry.no':
    case 'localhost.no':
      return 'NO';
    case 'livi.co.uk':
    case 'kry.co.uk':
    case 'localhost.co.uk':
    case 'embeddable.livi.it':
    case 'livi.member.test.lime.global':
    case 'livi.member.stage.lime.global':
    case 'livi.member.limeoncall.co.uk':
      return 'GB';
    case 'livi.fr':
    case 'kry.fr':
    case 'localhost.fr':
      return 'FR';

    default:
      return 'SE';
  }
};

const isKry = () => {
  switch (getDomain()) {
    case 'kry.se':
    case 'kry.no':
    case 'kry.co.uk':
    case 'kry.fr':
    case 'localhost.se':
    case 'localhost.no':
      return true;
    default:
      return false;
  }
};

const isLivi = () => !isKry();

// True for markets that were formerly Livi and have rebranded to Kry (kry.co.uk, kry.fr).
// Use this to conditionally render rebrand-specific UI distinct from original Kry markets (kry.se, kry.no).
const isKryRebrand = () => {
  const domain = getDomain();
  return domain === 'kry.co.uk' || domain === 'kry.fr';
};

const isDevEnvironment = () => {
  return process.env.NODE_ENV !== 'production';
};

const isProdEnvironment = () => {
  const host = window.location.host.toLowerCase();
  return (
    !isDevEnvironment() &&
    !host.includes('test') &&
    !host.includes('embeddable.livi.it') &&
    !host.includes(limeEnvs.test) &&
    !host.includes(limeEnvs.staging) &&
    !host.includes('localhost') &&
    !host.includes('amplifyapp')
  );
};

const getApiUrlForSubdomain = (subdomain: string, path: string) => {
  return `${getProtocol()}${subdomain}.${getDomain()}${path}`;
};

const getApiUrlFrance = (path: string) => {
  return `${getProtocol()}${getDomain()}${path}`;
};

const environment = {
  DOMAIN: getDomain(),
  COUNTRY: getCountry(),

  IS_KRY: isKry(),
  IS_LIVI: isLivi(),
  IS_KRY_REBRAND: isKryRebrand(),

  IS_DEV: isDevEnvironment(),
  IS_PROD: isProdEnvironment(),

  getApiUrlForSubdomain,
  getApiUrlFrance,
};

export default environment;
